import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isCocina, isStaffRole } from '../lib/adminRoles';
import { getKitchenOrders, markKitchenOrderAttended, formatApiDetail, resolveMediaUrl, staffKitchenPendingPrint, staffPrintAck } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ChefHat } from 'lucide-react';
import OrderKitchenTimer from '../components/OrderKitchenTimer';
import { orderSourceLabel } from '../lib/orderSourceLabel';
import { notifyOrdersChanged } from '../lib/orderSync';
import PrinterSlotPanel from '../components/PrinterSlotPanel';
import { printEscPosLines } from '../lib/thermalPrint/webBluetoothPrinter';
import { buildKitchenTicketLines } from '../lib/thermalPrint/escPos';

function lineMeta(ln) {
  const parts = [];
  if (ln.size_label) parts.push(ln.size_label);
  else if (ln.description) parts.push(ln.description);
  if (ln.half_specialty_name) parts.push(`Mitad: ${ln.half_specialty_name}`);
  const extras = (ln.extra_toppings || []).map((t) => t.name).filter(Boolean);
  if (extras.length) parts.push(`Extra: ${extras.join(', ')}`);
  if (ln.category_name) parts.push(ln.category_name);
  if (ln.notes) parts.push(`Notas: ${ln.notes}`);
  return parts.join(' — ') || '';
}

function formatGrams(grams) {
  const n = Number(grams);
  if (!Number.isFinite(n) || n <= 0) return '';
  return Number.isInteger(n) ? `${n} g` : `${n.toFixed(1)} g`;
}

function lineImageSrc(ln) {
  const url = ln?.image_url;
  if (!url) {
    const id = ln?.menu_item_id;
    if (id) return resolveMediaUrl(`/api/menu/items/${id}/image`);
    return '';
  }
  return resolveMediaUrl(url);
}

function IngredientList({ line }) {
  const recipe = Array.isArray(line.recipe) ? line.recipe : [];
  const names = (line.ingredients || []).filter(Boolean);

  if (recipe.length > 0) {
    return (
      <ul className="list-none space-y-1.5 pl-0">
        {recipe.map((row, i) => {
          const label = formatGrams(row.grams);
          return (
            <li
              key={`${row.name}-${i}`}
              className="text-lg font-semibold text-foreground leading-snug"
            >
              {row.name}
              {label ? (
                <span className="text-primary font-bold"> — {label}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  }

  if (names.length > 0) {
    return (
      <ul className="list-disc pl-5 space-y-1">
        {names.map((ing) => (
          <li key={ing} className="text-lg font-semibold">
            {ing}
          </li>
        ))}
      </ul>
    );
  }

  return null;
}

function kitchenNotesForDisplay(order) {
  const notes = String(order?.admin_notes || '').trim();
  const addr = String(order?.delivery_address || '').trim();
  if (!notes) return '';
  if (addr && notes.toLowerCase() === addr.toLowerCase()) return '';
  return notes;
}

function KitchenLineCard({ ln }) {
  const src = lineImageSrc(ln);
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="flex gap-4 items-start">
        <div className="shrink-0 w-32 h-32 sm:w-36 sm:h-36 rounded-xl overflow-hidden border-2 border-background shadow-sm bg-muted">
          {src ? (
            <img
              src={src}
              alt={ln.name || 'Producto'}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl bg-muted">
              🍕
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="font-bold text-xl leading-tight">
            {ln.qty}x {ln.name}
          </p>
          {lineMeta(ln) ? (
            <p className="text-sm text-muted-foreground">{lineMeta(ln)}</p>
          ) : null}
          <IngredientList line={ln} />
        </div>
      </div>
    </div>
  );
}

export default function AdminKitchen() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [queuePizzaCount, setQueuePizzaCount] = useState(0);
  const [queueOrderCount, setQueueOrderCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getKitchenOrders();
      setOrders(Array.isArray(data.orders) ? data.orders : []);
      setQueuePizzaCount(Number(data.queue_pizza_count) || 0);
      setQueueOrderCount(Number(data.queue_order_count) || 0);
      try {
        const pending = await staffKitchenPendingPrint();
        for (const o of pending || []) {
          try {
            await printEscPosLines('kitchen', buildKitchenTicketLines(o));
            await staffPrintAck(o.id, 'kitchen');
          } catch {
            /* impresora no lista */
          }
        }
      } catch {
        /* ignore print poll errors */
      }
    } catch {
      toast.error('No se pudo cargar la cola de cocina');
    } finally {
      setLoading(false);
    }
  }, []);

  const displayPizzaCount = useMemo(() => {
    if (queuePizzaCount > 0) return queuePizzaCount;
    return orders.reduce((sum, o) => {
      const lines = o.kitchen_lines || o.lines || [];
      return sum + lines.reduce((s, ln) => s + Math.max(1, Number(ln.qty) || 1), 0);
    }, 0);
  }, [orders, queuePizzaCount]);

  useEffect(() => {
    if (!authLoading && user && !isCocina(user.role) && !isStaffRole(user.role)) {
      navigate('/admin/login', { replace: true });
    }
    if (!authLoading && user && isStaffRole(user.role) && !isCocina(user.role) && user.role !== 'admin' && user.role !== 'super_admin') {
      navigate('/admin/pedidos', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user && (isCocina(user.role) || user.role === 'admin' || user.role === 'super_admin')) {
      load();
      const t = setInterval(load, 12000);
      return () => clearInterval(t);
    }
  }, [user, load]);

  const markDone = async (orderId) => {
    try {
      setBusyId(orderId);
      await markKitchenOrderAttended(orderId);
      notifyOrdersChanged();
      toast.success('Pedido enviado a caja — Pedidos se actualiza al instante');
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, 'No se pudo actualizar'));
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading || loading) {
    return <div className="text-muted-foreground animate-pulse p-8">Cargando cocina…</div>;
  }

  if (!user || (!isCocina(user.role) && user.role !== 'admin' && user.role !== 'super_admin')) {
    return null;
  }

  const orderCount = queueOrderCount > 0 ? queueOrderCount : orders.length;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-kitchen">
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-[200px]">
          <ChefHat className="w-8 h-8 text-primary shrink-0 mt-0.5" />
          <div>
            <h1 className="font-heading text-2xl font-bold">Cocina</h1>
            <p className="text-muted-foreground">
              Pedidos en preparación. Al terminar, márcalos atendidos para que caja agregue complementos si
              aplica.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <PrinterSlotPanel slot="kitchen" compact />
          <Card className="border-2 border-primary/40 bg-primary/5 shadow-md shrink-0 min-w-[140px]">
            <CardContent className="py-4 px-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Pizzas en fila
              </p>
              <p
                className="text-6xl sm:text-7xl font-black tabular-nums text-primary leading-none"
                aria-live="polite"
              >
                {displayPizzaCount}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {orderCount} pedido{orderCount === 1 ? '' : 's'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No hay pedidos en preparación en este momento.
          </CardContent>
        </Card>
      ) : (
        orders.map((o) => {
          const prepNotes = kitchenNotesForDisplay(o);
          return (
          <Card key={o.id} className="border-2 border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <CardTitle className="text-xl">
                  #{o.order_number}{' '}
                  <span className="text-base font-normal text-muted-foreground">{o.customer_name}</span>
                </CardTitle>
                <OrderKitchenTimer order={o} size="lg" />
              </div>
              {o.delivery_type === 'pickup' || o.source === 'counter' ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {o.delivery_type === 'pickup' ? (
                    <Badge variant="secondary">Recoger</Badge>
                  ) : null}
                  {o.source === 'counter' ? (
                    <Badge className="bg-sage-100 text-sage-900 border-sage-300">
                      {orderSourceLabel(o.source)}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-4">
              {(o.kitchen_lines || o.lines || []).map((ln, idx) => (
                <KitchenLineCard key={`${o.id}-${ln.menu_item_id || idx}`} ln={ln} />
              ))}
              {prepNotes ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Notas preparación: {prepNotes}
                </p>
              ) : null}
              <Button
                className="w-full rounded-full"
                disabled={busyId === o.id}
                onClick={() => markDone(o.id)}
              >
                {busyId === o.id ? 'Guardando…' : 'Despachar a caja'}
              </Button>
            </CardContent>
          </Card>
          );
        })
      )}
    </div>
  );
}
